import { MessageCircle, Bell, BookOpen, Star, CheckCircle2, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';

const JoinCommunity = () => {
  const { isDark } = useTheme();
  const communityOptions = [
    {
      icon: MessageCircle,
      title: 'Discussion Group',
      description: 'Join our active WhatsApp community where builders discuss ideas, share resources, form teams, and help each other grow.',
      features: [
        'Daily discussions and idea sharing',
        'Find teammates for your projects',
        'Get real-time help and feedback',
        'Network with 150+ builders'
      ],
      cta: 'Join Discussion Group',
      link: 'https://chat.whatsapp.com/HkxDeCqP5pxHx2zJ0hm5I5?mode=wwt',
      color: 'green',
      bgColor: 'from-green-400 to-green-500'
    },
    {
      icon: Bell,
      title: 'Updates Channel',
      description: 'Follow our channel for weekly challenges, project launches, builder stories, and announcements.',
      features: [
        'Weekly problem statements',
        'Project launch updates',
        'Builder highlights and stories',
        'Community events & workshops'
      ],
      cta: 'Follow Updates Channel',
      link: '#',
      color: 'blue',
      bgColor: 'from-blue-400 to-blue-500'
    },
    {
      icon: BookOpen,
      title: 'Builder Resources',
      description: 'Access all project frameworks, tools, and ready-to-use templates that help you go from idea to launch faster.',
      features: [
        'Project frameworks and trackers',
        'Design, development, and research tools',
        'Pitch deck & template kits',
        'Monthly updated resource hub'
      ],
      cta: 'Access Resources',
      link: '#',
      color: 'purple',
      bgColor: 'from-purple-400 to-purple-500'
    },
    {
      icon: Star,
      title: 'Showcase & Updates',
      description: 'Explore what our builders have created — from problem statements to prototypes.',
      features: [
        'Project of the Month',
        'Demo Zone (videos/reels)',
        'Builder stories and reflections',
        'Community achievements'
      ],
      cta: 'View Highlights',
      link: '#',
      color: 'orange',
      bgColor: 'from-orange-400 to-orange-500'
    }
  ];

  return (
    <motion.div 
      className={`pt-20 min-h-screen transition-colors duration-300 ${
        isDark ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e]' : 'bg-gray-50'
      }`}
      data-testid="join-community-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-br from-purple-900/30 via-transparent to-purple-900/30' 
          : 'bg-gradient-to-br from-indigo-600 to-purple-700'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 mb-8"
          >
            <Users className="w-4 h-4 text-purple-300" />
            <span className="text-sm font-medium text-purple-200">
              Community Hub
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-6 text-white"
            data-testid="community-page-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Join our Community
          </motion.h1>

          {/* Subheading */}
          <motion.p 
            className={`text-xl max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-white/90'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            At Prodizzy, every idea becomes a community-driven project. 
            Choose your space, join the discussion, and start building.
          </motion.p>
        </div>
      </section>

      {/* Community Options */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="community-options-grid">
            {communityOptions.map((option, index) => {
              const Icon = option.icon;

              return (
                <motion.div
                  key={index}
                  className={`rounded-2xl shadow-lg overflow-hidden border-2 transition-all ${
                    isDark
                      ? 'bg-white/5 border-purple-500/30 hover:border-purple-400'
                      : 'bg-white border-indigo-100 hover:border-indigo-400'
                  }`}
                  data-testid={`community-card-${index}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                >
                  <div className={`p-6 border-b-2 ${
                    isDark 
                      ? 'border-purple-500/20' 
                      : 'border-indigo-200'
                  }`}>
                    <motion.div 
                      className={`mb-4 bg-gradient-to-br ${option.bgColor} rounded-full w-16 h-16 flex items-center justify-center`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <Icon size={32} className="text-white" />
                    </motion.div>
                    <h2 className={`text-2xl font-bold mb-3 ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>{option.title}</h2>
                    <p className={`mb-6 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{option.description}</p>

                    {/* Features Checklist */}
                    <ul className="space-y-3 mb-6">
                      {option.features.map((feature, idx) => (
                        <motion.li 
                          key={idx} 
                          className="flex items-start" 
                          data-testid={`community-feature-${index}-${idx}`}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.1 }}
                        >
                          <CheckCircle2 className="text-green-400 mr-3 flex-shrink-0 mt-0.5" size={20} />
                          <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{feature}</span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <motion.a
                      href={option.link}
                      className={`block text-center text-white px-6 py-3 rounded-full font-semibold transition shadow-lg hover:shadow-xl bg-gradient-to-r ${option.bgColor}`}
                      data-testid={`community-cta-${index}`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {option.cta} →
                    </motion.a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className={`py-16 ${
        isDark ? 'bg-transparent' : 'bg-white'
      }`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`text-4xl font-bold text-center mb-12 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Why Join?
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Collaborative Learning',
                description: 'Learn from experienced builders, share knowledge, and grow together in a supportive environment.'
              },
              {
                title: 'Build Your Network',
                description: 'Connect with like-minded innovators, potential co-founders, and industry experts.'
              },
              {
                title: 'Real Impact',
                description: 'Turn your ideas into real projects that solve problems and create value for communities.'
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-6 rounded-xl ${
                  isDark 
                    ? 'bg-white/5 border border-purple-500/30' 
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <h3 className={`text-xl font-bold mb-3 ${
                  isDark ? 'text-purple-300' : 'text-indigo-600'
                }`}>
                  {benefit.title}
                </h3>
                <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className={`py-16 border-t ${
        isDark 
          ? 'bg-gradient-to-r from-purple-900/50 to-purple-800/50 border-purple-500/20' 
          : 'bg-gradient-to-r from-indigo-600 to-purple-600 border-indigo-800 border-t-4'
      } text-white`} data-testid="community-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className={`rounded-3xl p-8 border-2 ${
              isDark ? 'bg-white/5 border-purple-500/30' : 'bg-white/10 border-white/20'
            }`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.h2 
              className={`text-3xl font-bold mb-4 pb-4 ${
                isDark ? 'border-b-2 border-purple-500/50' : 'border-b-2 border-white/30'
              }`}
            >
              Ready to make an impact?
            </motion.h2>
          <motion.p 
            className={`text-xl mb-8 ${isDark ? 'text-gray-200' : 'text-white'}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Join over 50,000 builders who are turning ideas into reality.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.a
              href="https://chat.whatsapp.com/HkxDeCqP5pxHx2zJ0hm5I5?mode=wwt"
              target="_blank"
              rel="noopener noreferrer"
              className={`px-8 py-3 rounded-full font-semibold transition shadow-lg hover:shadow-xl ${
                isDark
                  ? 'bg-purple-500 text-white hover:bg-purple-600'
                  : 'bg-white text-indigo-600 hover:bg-gray-100'
              }`}
              data-testid="community-bottom-cta-whatsapp"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Join WhatsApp Community
            </motion.a>
            <motion.a
              href="https://www.linkedin.com/company/prodizzy/"
              target="_blank"
              rel="noopener noreferrer"
              className={`border-2 px-8 py-3 rounded-full font-semibold transition shadow-lg hover:shadow-xl ${
                isDark
                  ? 'border-purple-400 text-purple-300 hover:bg-purple-500/20'
                  : 'bg-transparent border-white text-white hover:bg-white hover:text-indigo-600'
              }`}
              data-testid="community-bottom-cta-telegram"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Follow on LinkedIn
            </motion.a>
          </motion.div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default JoinCommunity;
